import React, { useState } from "react";

import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Switch,
  FormControlLabel,
  ListItemText,
} from "@mui/material";
import { ScreenShareOutlined } from "@mui/icons-material";
import GifIcon from "@mui/icons-material/Gif";
import MovieIcon from "@mui/icons-material/Movie";

import ModernModal from "../../common/ModernModal";
import {
  ExportVideoCompressionLevel,
  ExportVideoResolution,
} from "../../EditShortVideo/types";
import { EDITOR_TOKENS } from "../editorTokens";

type ExportFormat = "gif" | "mp4";

const FORMAT_OPTIONS: {
  value: ExportFormat;
  label: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: "gif",
    label: "GIF",
    icon: <GifIcon />,
    description: "Animated, looping clip",
  },
  {
    value: "mp4",
    label: "MP4",
    icon: <MovieIcon />,
    description: "Video with audio",
  },
];

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  onExport?: (params: {
    format: ExportFormat;
    resolution: ExportVideoResolution;
    compressionLevel: ExportVideoCompressionLevel;
    fps: number;
    includeSubtitles: boolean;
  }) => void;
}

export default function ExportModal({
  open,
  onClose,
  onExport,
}: ExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>("mp4");
  const [compressionLevel, setCompressionLevel] = useState(
    ExportVideoCompressionLevel.BALANCED,
  );
  const [resolution, setResolution] = useState(ExportVideoResolution["1080p"]);
  const [fps, setFps] = useState<number>(30);
  const [includeSubtitles, setIncludeSubtitles] = useState(true);

  const handleExportClick = () => {
    onExport?.({
      format,
      resolution,
      compressionLevel,
      fps,
      includeSubtitles,
    });
    onClose();
  };

  return (
    <ModernModal
      open={open}
      onClose={onClose}
      title="Export Audiogram"
      maxWidth="sm"
      actions={
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportClick}
            startIcon={<ScreenShareOutlined />}
            sx={{
              background: `linear-gradient(135deg, ${EDITOR_TOKENS.success}, ${EDITOR_TOKENS.successDark})`,
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              fontWeight: 600,
              "&:hover": {
                background: `linear-gradient(135deg, ${EDITOR_TOKENS.successDark}, #047857)`,
                boxShadow: "0 6px 20px rgba(16, 185, 129, 0.4)",
                transform: "translateY(-1px)",
              },
            }}
          >
            Export {format.toUpperCase()}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Format */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
          >
            Format
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {FORMAT_OPTIONS.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                sx={{
                  flex: 1,
                  border: "1.5px solid",
                  borderColor:
                    format === opt.value
                      ? EDITOR_TOKENS.accent
                      : "rgba(0, 0, 0, 0.12)",
                  borderRadius: "10px",
                  p: 2,
                  cursor: "pointer",
                  textAlign: "center",
                  backgroundColor:
                    format === opt.value
                      ? EDITOR_TOKENS.accentSofter
                      : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: EDITOR_TOKENS.accent,
                    backgroundColor: "rgba(37, 99, 235, 0.03)",
                  },
                }}
              >
                <Box
                  sx={{
                    color:
                      format === opt.value
                        ? EDITOR_TOKENS.accent
                        : "text.secondary",
                    mb: 0.5,
                    "& .MuiSvgIcon-root": { fontSize: 28 },
                  }}
                >
                  {opt.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color:
                      format === opt.value
                        ? EDITOR_TOKENS.accent
                        : "text.primary",
                  }}
                >
                  {opt.label}
                </Typography>
                <Typography
                  sx={{ fontSize: "11px", color: "text.secondary", mt: 0.25 }}
                >
                  {opt.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Compression Level */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
          >
            Quality & Speed
          </Typography>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Compression Level</InputLabel>
            <Select
              value={compressionLevel}
              onChange={(e) =>
                setCompressionLevel(
                  e.target.value as ExportVideoCompressionLevel,
                )
              }
              label="Compression Level"
            >
              <MenuItem value={ExportVideoCompressionLevel.HIGHER_QUALITY}>
                <ListItemText
                  primary="Higher Quality"
                  secondary="Best visual quality, longer render times"
                />
              </MenuItem>
              <MenuItem value={ExportVideoCompressionLevel.BALANCED}>
                <ListItemText
                  primary="Balanced"
                  secondary="Good balance between quality and speed"
                />
              </MenuItem>
              <MenuItem value={ExportVideoCompressionLevel.VERY_FAST}>
                <ListItemText
                  primary="Very Fast"
                  secondary="Fast render times, lower quality"
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Resolution & Platform */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
          >
            Resolution & Platform
          </Typography>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Resolution</InputLabel>
            <Select
              value={resolution}
              onChange={(e) =>
                setResolution(e.target.value as ExportVideoResolution)
              }
              label="Resolution"
            >
              <MenuItem value={ExportVideoResolution["320p"]}>
                <ListItemText
                  primary="320p"
                  secondary="Low resolution, faster upload and processing"
                />
              </MenuItem>
              <MenuItem value={ExportVideoResolution["480p"]}>
                <ListItemText
                  primary="480p"
                  secondary="Standard definition, faster processing"
                />
              </MenuItem>
              <MenuItem value={ExportVideoResolution["720p"]}>
                <ListItemText
                  primary="720p"
                  secondary="High definition, standard for online videos"
                />
              </MenuItem>
              <MenuItem value={ExportVideoResolution["1080p"]}>
                <ListItemText
                  primary="1080p"
                  secondary="Full HD, good quality for most purposes"
                />
              </MenuItem>
              <MenuItem value={ExportVideoResolution["4k"]}>
                <ListItemText
                  primary="4K"
                  secondary="Ultra HD, highest quality with long render times"
                />
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem value={ExportVideoResolution.instagram}>
                <ListItemText
                  primary="Instagram"
                  secondary="Optimized for Instagram stories"
                />
              </MenuItem>
              <MenuItem value={ExportVideoResolution.tiktok}>
                <ListItemText
                  primary="TikTok"
                  secondary="Optimized for TikTok format"
                />
              </MenuItem>
              <MenuItem value={ExportVideoResolution["youtube-shorts"]}>
                <ListItemText
                  primary="YouTube Shorts"
                  secondary="Optimized for YouTube Shorts"
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Frame Rate */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
          >
            Frame Rate
          </Typography>
          <FormControl fullWidth variant="outlined">
            <InputLabel>FPS</InputLabel>
            <Select
              value={fps}
              onChange={(e) => setFps(e.target.value as number)}
              label="FPS"
            >
              <MenuItem value={24}>
                <ListItemText
                  primary="24 FPS"
                  secondary="Cinematic feel, ideal for storytelling content"
                />
              </MenuItem>
              <MenuItem value={30}>
                <ListItemText
                  primary="30 FPS"
                  secondary="Standard for most online videos"
                />
              </MenuItem>
              <MenuItem value={60}>
                <ListItemText
                  primary="60 FPS"
                  secondary="Smooth motion, great for dynamic content"
                />
              </MenuItem>
              <MenuItem value={120}>
                <ListItemText
                  primary="120 FPS"
                  secondary="Ultra smooth, excellent for slow-motion scenes"
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Include Subtitles */}
        <Box
          sx={{
            p: 2,
            bgcolor: "rgba(0,0,0,0.02)",
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={includeSubtitles}
                onChange={(e) => setIncludeSubtitles(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Include Subtitles
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add captions to your exported audiogram for better
                  accessibility
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
    </ModernModal>
  );
}
